import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Sparkles, Crown, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { devLog, devError } from '../utils/logger';
import { NORWEGIAN_COUNTIES } from '../utils/norwegian-locations';
import { supabase } from '../utils/supabase/client';
import { canCreateGig } from '../utils/accessPolicy';
import { validateText } from '../utils/profanity-filter';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { FullscreenShell } from './layout/FullscreenShell';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { CONTENT_IMAGE_SPEC_4_3 } from '../utils/media/imageCrop';
import { ConfirmDialog } from './ConfirmDialog';

interface CreateGigModalProps {
  gig?: any; // For edit mode
  onClose: () => void;
  onSuccess: () => void;
  userProfile: any;
  accessToken: string;
  /** When provided, used instead of location.hash for "go to subscription" (avoids hash; use ?view=subscription) */
  onNavigateToSubscription?: () => void;
}

const CLOSE_ANIMATION_MS = 220;

export function CreateGigModal({ gig, onClose, onSuccess, userProfile, accessToken, onNavigateToSubscription }: CreateGigModalProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen modal: hide header/footer while open
  const isEditMode = !!gig;
  
  const [title, setTitle] = useState(gig?.title || '');
  const [description, setDescription] = useState(gig?.description || '');
  const [skills, setSkills] = useState<string[]>(gig?.skills || []);
  const [county, setCounty] = useState(gig?.county || '');
  const [budget, setBudget] = useState(gig?.budget || '');
  const [expiresAt, setExpiresAt] = useState(gig?.expiresAt ? gig.expiresAt.split('T')[0] : '');
  const [skillInput, setSkillInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState(gig?.imageUrl || gig?.image_url || gig?.image || '');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const imageUrlRef = useRef(gig?.imageUrl || gig?.image_url || gig?.image || '');
  const pendingImageUploadRef = useRef<Promise<string | null> | null>(null);
  const { openCrop, cropModal } = useImageCropModal();
  const { isDirty, markDirty, resetDirty, setDirty } = useDirtyFormGuard(false);

  const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const isAndroidPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('android-pwa');
  const isAndroid = typeof window !== 'undefined' && typeof navigator !== 'undefined' && typeof document !== 'undefined' && !document.documentElement.classList.contains('ios-pwa') && (document.documentElement.classList.contains('android-pwa') || /android/i.test(navigator.userAgent));

  const closeWithAnimation = useCallback(() => {
    if (isSubmitting) return;
    if (isClosing) return;
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [isClosing, isSubmitting, onClose]);

  const handleRestoreDraft = useCallback((payload: {
    title: string;
    description: string;
    skills: string[];
    county: string;
    budget: string;
    expiresAt: string;
    imageUrl: string;
  }) => {
    setTitle(payload?.title || '');
    setDescription(payload?.description || '');
    setSkills(Array.isArray(payload?.skills) ? payload.skills : []);
    setCounty(payload?.county || '');
    setBudget(payload?.budget || '');
    setExpiresAt(payload?.expiresAt || '');
    const restoredImageUrl = payload?.imageUrl || '';
    setImageUrl(restoredImageUrl);
    imageUrlRef.current = restoredImageUrl;
    setDirty(true);
  }, [setDirty]);

  const { clearDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:create-gig:${userProfile?.id ?? 'me'}:${gig?.id ?? 'new'}`,
    enabled: true,
    value: { title, description, skills, county, budget, expiresAt, imageUrl },
    onRestore: handleRestoreDraft,
  });

  useBeforeUnloadDirty(isDirty && !isSubmitting);

  const requestClose = useCallback(() => {
    if (isSubmitting) return;
    if (isClosing) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    clearDraft();
    resetDirty();
    closeWithAnimation();
  }, [isSubmitting, isClosing, isDirty, clearDraft, resetDirty, closeWithAnimation]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    clearDraft();
    resetDirty();
    closeWithAnimation();
  }, [clearDraft, resetDirty, closeWithAnimation]);

  const navigateToSubscription = useCallback(() => {
    clearDraft();
    resetDirty();
    closeWithAnimation();
    window.setTimeout(() => {
      if (onNavigateToSubscription) {
        onNavigateToSubscription();
      } else {
        window.location.hash = 'subscription';
      }
    }, CLOSE_ANIMATION_MS);
  }, [closeWithAnimation, onNavigateToSubscription, clearDraft, resetDirty]);

  // Centralized policy: creation requires employer_plus. Edit mode stays allowed for existing gig owners.
  const hasAccess = isEditMode || canCreateGig(userProfile);

  // Browser history for back button
  useEffect(() => {
    window.history.pushState({ modal: 'create-gig' }, '');
    
    const handlePopState = (e: PopStateEvent) => {
      // If history moved TO this modal state, keep it open.
      if (e.state?.modal === 'create-gig') return;
      requestClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [requestClose]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleAddSkill = (skill: string) => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;
    
    if (skills.includes(trimmedSkill)) {
      toast.error('Denne ferdigheten er allerede lagt til');
      return;
    }
    
    if (skills.length >= 5) {
      toast.error('Maks 5 ferdigheter tillatt');
      return;
    }
    
    setSkills([...skills, trimmedSkill]);
    setSkillInput('');
    markDirty();
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter(s => s !== skillToRemove));
    markDirty();
  };

  const handleSubmit = async () => {
    if (!hasAccess) {
      toast.error('Kun Arbeidsgiver Pluss kan opprette oppdrag');
      navigateToSubscription();
      return;
    }

    // Validation
    if (!title.trim()) {
      toast.error('Tittel er påkrevd');
      return;
    }

    if (title.length > 100) {
      toast.error('Tittel kan ikke være lengre enn 100 tegn');
      return;
    }

    if (!description.trim()) {
      toast.error('Beskrivelse er påkrevd');
      return;
    }

    if (description.length > 2000) {
      toast.error('Beskrivelse kan ikke være lengre enn 2000 tegn');
      return;
    }

    if (skills.length === 0) {
      toast.error('Minst én ferdighet må velges');
      return;
    }
    const titleValidation = validateText(title, 'Tittel');
    if (!titleValidation.isValid) {
      toast.error(titleValidation.error || 'Tittel inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    const descriptionValidation = validateText(description, 'Beskrivelse');
    if (!descriptionValidation.isValid) {
      toast.error(descriptionValidation.error || 'Beskrivelse inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    for (const skill of skills) {
      const skillValidation = validateText(skill, 'Ferdighet');
      if (!skillValidation.isValid) {
        toast.error(skillValidation.error || 'Ferdighet inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }
    }

    if (!county) {
      toast.error('Fylke er påkrevd');
      return;
    }

    if (budget && budget.length > 50) {
      toast.error('Budsjett kan ikke være lengre enn 50 tegn');
      return;
    }
    // Hard guard: await any in-flight upload promise before building payload.
    if (pendingImageUploadRef.current) {
      const pendingUploadUrl = await pendingImageUploadRef.current;
      if (pendingUploadUrl) {
        imageUrlRef.current = pendingUploadUrl;
        setImageUrl(pendingUploadUrl);
      }
    } else if (isUploadingImage) {
      toast.error('Vent til bildet er ferdig lastet opp før du publiserer');
      return;
    }

    setIsSubmitting(true);

    try {
      const resolvedImageUrl = (imageUrlRef.current || imageUrl || '').trim() || null;
      if (image && !resolvedImageUrl) {
        throw new Error('Bilde ble lastet opp, men URL mangler. Prøv igjen.');
      }
      const payload = {
        title: title.trim(),
        description: description.trim(),
        skills,
        county,
        budget: budget.trim() || null,
        expiresAt: expiresAt || null,
        imageUrl: resolvedImageUrl,
        image_url: resolvedImageUrl
      };

      const url = isEditMode
        ? `${API_BASE}/gigs/${gig.id}`
        : `${API_BASE}/gigs`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresUpgrade) {
          toast.error(data.error || 'Du må oppgradere til Arbeidsgiver Pluss');
          navigateToSubscription();
          return;
        }
        throw new Error(data.error || 'Kunne ikke lagre oppdrag');
      }

      // Bulletproof persistence: repair once if API response still lacks image URL.
      const persistedImageUrl = data?.gig?.imageUrl || data?.gig?.image_url || null;
      if (resolvedImageUrl && !persistedImageUrl) {
        const gigId = data?.gig?.id || gig?.id;
        if (!gigId) {
          throw new Error('Oppdrag lagret, men mangler ID for å koble bilde.');
        }

        const repairResponse = await fetch(`${API_BASE}/gigs/${gigId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(payload)
        });
        const repairData = await repairResponse.json();
        if (!repairResponse.ok) {
          throw new Error(repairData?.error || 'Kunne ikke koble bilde til oppdraget.');
        }

        const repairedImageUrl = repairData?.gig?.imageUrl || repairData?.gig?.image_url || null;
        if (!repairedImageUrl) {
          throw new Error('Oppdrag lagret, men bildet ble ikke koblet. Prøv å redigere og lagre på nytt.');
        }
      }

      devLog(`✅ Gig ${isEditMode ? 'updated' : 'created'} successfully:`, data);
      toast.success(isEditMode ? 'Oppdrag oppdatert!' : 'Oppdrag publisert!');
      clearDraft();
      resetDirty();
      onSuccess();
      requestClose();

    } catch (error) {
      devError('Error saving gig:', error);
      toast.error(error instanceof Error ? error.message : 'Kunne ikke lagre oppdrag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const sourceFile = e.target.files[0];
    const sourceExt = sourceFile.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif'];

    if (!validExtensions.includes(sourceExt || '')) {
      toast.error('Kun bildefiler er tillatt (jpg, jpeg, png, gif)');
      return;
    }

    let file = sourceFile;
    try {
      file = await openCrop(sourceFile, CONTENT_IMAGE_SPEC_4_3);
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) {
        return;
      }
      toast.error('Kunne ikke beskjære bildet.');
      return;
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    setImage(file);
    setIsUploadingImage(true);
    const uniqueId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const storagePath = `public/${uniqueId}.${fileExt}`;

    const uploadPromise = (async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('gig-images')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        devError('Error uploading image:', error);
        toast.error('Kunne ikke laste opp bilde');
        return null;
      }

      const { data: publicData } = supabase.storage.from('gig-images').getPublicUrl(data.path);
      return publicData.publicUrl || null;
    })();

    pendingImageUploadRef.current = uploadPromise;
    try {
      const uploadedUrl = await uploadPromise;
      if (!uploadedUrl) return;
      imageUrlRef.current = uploadedUrl;
      setImageUrl(uploadedUrl);
      markDirty();
      toast.success('Bilde lastet opp');
    } finally {
      if (pendingImageUploadRef.current === uploadPromise) {
        pendingImageUploadRef.current = null;
      }
      setIsUploadingImage(false);
    }
  };

  const modalTitle = isEditMode ? 'Rediger oppdrag' : 'Opprett nytt oppdrag';

  return (
    <>
    <ConfirmDialog
      isOpen={showDiscardConfirm}
      onClose={() => setShowDiscardConfirm(false)}
      onConfirm={confirmDiscard}
      title="Forkaste endringer?"
      message="Du har ulagrede endringer i dette skjemaet. Vil du forkaste disse?"
      confirmText="Forkast"
      cancelText="Fortsett redigering"
      variant="warning"
    />
    {cropModal}
    <FullscreenShell
      title={modalTitle}
      onClose={requestClose}
      onBack={requestClose}
      compactSpacer={isIosPwa || isAndroidPwa}
      overlayClassName="gig-modal-overlay gig-modal-overlay-create"
      onDesktopBackdropClick={requestClose}
      overlayMotionProps={{
        initial: { opacity: 0 },
        animate: { opacity: isClosing ? 0 : 1 },
        transition: { duration: 0.18, ease: 'easeOut' },
      }}
      contentClassName="edit-profile-drawer-content space-y-6 pb-8 xl:pb-8"
    >
      {!hasAccess ? (
        // Upgrade prompt
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-200">
          <div className="flex items-start gap-3 mb-4">
            <Crown className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-purple-900 mb-2">Oppgrader til Arbeidsgiver Pluss</h3>
              <p className="text-purple-800 text-sm mb-3">
                For å opprette oppdrag må du ha Arbeidsgiver Pluss-abonnement. Med Arbeidsgiver Pluss får du blant annet muligheten til å legge ut oppdrag:
              </p>
              <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
                <li>Legge ut oppdrag – publiser oppdrag som er synlig for alle brukere</li>
                <li>Publiser ubegrenset antall oppdrag</li>
                <li>Få interesse fra brukere med Talentfunn Pluss, Arbeidsgiver Basis eller Arbeidsgiver Pluss</li>
                <li>Alle fordeler fra Arbeidsgiver Basis (meldingstilgang og favoritter)</li>
              </ul>
            </div>
          </div>
          <button
            onClick={navigateToSubscription}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
          >
            Se Arbeidsgiver Pluss-abonnement
          </button>
        </div>
      ) : (
        <>
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { markDirty(); setTitle(e.target.value); }}
                  placeholder="F.eks: Grafisk designer til ny logo"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  maxLength={100}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">{title.length}/100 tegn</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Beskrivelse *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => { markDirty(); setDescription(e.target.value); }}
                  placeholder="Beskriv oppdraget i detalj. Hva skal gjøres? Når? Hvor?"
                  className="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  maxLength={2000}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">{description.length}/2000 tegn</p>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Ønskede ferdigheter * (Maks 5)
                </label>
                
                {/* Selected skills */}
                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {skills.map((skill, index) => (
                      <div
                        key={index}
                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-2"
                      >
                        {skill}
                        <button
                          onClick={() => handleRemoveSkill(skill)}
                          className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                          disabled={isSubmitting}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Custom skill input */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSkill(skillInput);
                      }
                    }}
                    placeholder="Legg til egen ferdighet..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    disabled={skills.length >= 5 || isSubmitting}
                  />
                  <button
                    onClick={() => handleAddSkill(skillInput)}
                    disabled={!skillInput.trim() || skills.length >= 5 || isSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Fylke *
                </label>
                <select
                  value={county}
                  onChange={(e) => { markDirty(); setCounty(e.target.value); }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isSubmitting}
                >
                  <option value="">Velg fylke</option>
                  {NORWEGIAN_COUNTIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Budsjett (valgfritt)
                </label>
                <input
                  type="text"
                  value={budget}
                  onChange={(e) => { markDirty(); setBudget(e.target.value); }}
                  placeholder='F.eks: "15 000 kr" eller "Etter avtale"'
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  maxLength={50}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">{budget.length}/50 tegn</p>
              </div>

              {/* Deadline */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Frist (valgfritt)
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => { markDirty(); setExpiresAt(e.target.value); }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isSubmitting}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Last opp bilde (valgfritt)
                </label>
                <p className="text-sm text-gray-500 mb-2">Du får beskjære bildet i neste steg. Anbefalt format: 4:3.</p>
                {isUploadingImage && (
                  <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700">
                    Laster opp bilde...
                  </div>
                )}
                {imageUrl ? (
                  <div className="relative">
                    <img
                      src={imageUrl}
                      alt="Oppdragsbilde"
                      className="w-full aspect-[4/3] object-cover rounded-2xl border-2 border-purple-300"
                    />
                    <button
                      onClick={() => {
                        markDirty();
                        imageUrlRef.current = '';
                        setImageUrl('');
                      }}
                      className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg transition-colors"
                      aria-label="Fjern bilde"
                      disabled={isUploadingImage || isSubmitting}
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-purple-600 bg-opacity-90 text-white rounded-full text-sm font-medium">
                      Oppdragsbilde
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload"
                    className={`flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-purple-400 rounded-2xl transition-all ${
                      isUploadingImage || isSubmitting
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer hover:border-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    <Upload className="w-12 h-12 text-purple-500 mb-2" />
                    <p className="text-purple-700 font-medium">{isUploadingImage ? 'Laster opp bilde...' : 'Klikk for å laste opp bilde'}</p>
                    <p className="text-purple-400 text-sm">PNG, JPG eller JPEG</p>
                  </label>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isSubmitting || isUploadingImage}
                />
              </div>

              {/* Action buttons – samme struktur som Rediger profil (pt-6 border-t, pb-safe) */}
              <div className="pt-6 border-t-2 border-purple-200">
                <div className="flex flex-wrap gap-4 pb-safe">
                  <button
                    type="button"
                    onClick={requestClose}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all"
                    disabled={isSubmitting || isUploadingImage}
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isUploadingImage || !title.trim() || !description.trim() || skills.length === 0 || !county}
                    className={`flex-1 min-w-[160px] px-6 py-3 rounded-xl font-semibold transition-all ${
                      isSubmitting || isUploadingImage || !title.trim() || !description.trim() || skills.length === 0 || !county
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                    }`}
                  >
                    {isUploadingImage
                      ? 'Laster opp bilde...'
                      : isSubmitting
                      ? (isEditMode ? 'Lagrer endringer...' : 'Publiserer oppdrag...')
                      : (isEditMode ? 'Lagre endringer' : 'Publiser oppdrag')
                    }
                  </button>
                </div>
              </div>
        </>
      )}
    </FullscreenShell>
    </>
  );
}
